import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import Hls from 'hls.js';
import {
  Maximize,
  Minimize,
  Pause,
  Play,
  Settings,
  SkipBack,
  SkipForward,
  Volume1,
  Volume2,
  VolumeX,
} from 'lucide-react';
import type { HLSLevel } from '../types';
import { formatTime } from '../lib/utils';

interface PlayerControlsProps {
  videoRef: RefObject<HTMLVideoElement>;
  containerRef: RefObject<HTMLDivElement>;
  hls: Hls | null;
  levels: HLSLevel[];
  currentLevel: number;
}

type PrefixedFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => void;
};

type PrefixedFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  msFullscreenElement?: Element | null;
  msExitFullscreen?: () => void;
};

type MobileFullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitEnterFullScreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};

function getFullscreenElement(): Element | null {
  const doc = document as PrefixedFullscreenDocument;
  return document.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement || null;
}

export const PlayerControls: React.FC<PlayerControlsProps> = ({
  videoRef,
  containerRef,
  hls,
  levels,
  currentLevel,
}) => {
  const progressRef = useRef<HTMLDivElement>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [isSeeking, setIsSeeking] = useState(false);
  const [showQuality, setShowQuality] = useState(false);
  const [feedback, setFeedback] = useState<'play' | 'pause' | null>(null);
  const [isAutoMode, setIsAutoMode] = useState(true);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState(0);

  const showQualityRef = useRef(showQuality);
  const isSeekingRef = useRef(isSeeking);
  showQualityRef.current = showQuality;
  isSeekingRef.current = isSeeking;

  const showControls = useCallback(() => setVisible(true), []);

  const hideControls = useCallback(() => {
    if (!videoRef.current?.paused && !showQualityRef.current && !isSeekingRef.current) {
      setVisible(false);
    }
  }, [videoRef]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      void video.play();
      setFeedback('play');
    } else {
      video.pause();
      setFeedback('pause');
    }

    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(null), 500);
  }, [videoRef]);

  const enterMobileVideoFullscreen = useCallback(() => {
    const video = videoRef.current as MobileFullscreenVideo | null;
    if (video?.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
      setIsFullscreen(true);
      return true;
    }
    if (video?.webkitEnterFullScreen) {
      video.webkitEnterFullScreen();
      setIsFullscreen(true);
      return true;
    }
    return false;
  }, [videoRef]);

  const toggleFullscreen = useCallback(() => {
    if (!getFullscreenElement()) {
      const container = containerRef.current as PrefixedFullscreenElement | null;
      let requestResult: Promise<void> | void;

      if (container?.requestFullscreen) {
        requestResult = container.requestFullscreen();
      } else if (container?.webkitRequestFullscreen) {
        requestResult = container.webkitRequestFullscreen();
      } else if (container?.msRequestFullscreen) {
        requestResult = container.msRequestFullscreen();
      } else {
        enterMobileVideoFullscreen();
        return;
      }

      if (requestResult && typeof requestResult.catch === 'function') {
        void requestResult.catch(() => {
          enterMobileVideoFullscreen();
        });
      }
      return;
    }

    const doc = document as PrefixedFullscreenDocument;
    if (document.exitFullscreen) {
      void document.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      void doc.webkitExitFullscreen();
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen();
    }
  }, [containerRef, enterMobileVideoFullscreen]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (video) video.muted = !video.muted;
  }, [videoRef]);

  const handleVolume = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const value = Number(event.target.value);
    video.volume = value;
    video.muted = value === 0;
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      setIsPlaying(true);
      if (!isHoveringRef.current) setVisible(false);
    };
    const onPause = () => {
      setIsPlaying(false);
      setVisible(true);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setVisible(true);
    };
    const onDuration = () => setDuration(video.duration);
    const onVolume = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };
    const onTime = () => {
      setCurrentTime(video.currentTime);
      if (video.buffered.length > 0 && video.duration > 0) {
        setBuffered((video.buffered.end(video.buffered.length - 1) / video.duration) * 100);
      }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('durationchange', onDuration);
    video.addEventListener('volumechange', onVolume);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('durationchange', onDuration);
      video.removeEventListener('volumechange', onVolume);
    };
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef.current as MobileFullscreenVideo | null;
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(getFullscreenElement() || video?.webkitDisplayingFullscreen));
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    video?.addEventListener('webkitbeginfullscreen', onFullscreenChange);
    video?.addEventListener('webkitendfullscreen', onFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      video?.removeEventListener('webkitbeginfullscreen', onFullscreenChange);
      video?.removeEventListener('webkitendfullscreen', onFullscreenChange);
    };
  }, [videoRef]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const video = videoRef.current;
      const target = event.target as HTMLElement | null;
      if (!video || target?.tagName === 'INPUT') return;

      switch (event.code) {
        case 'Space':
        case 'KeyK':
          event.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          video.currentTime = Math.max(0, video.currentTime - 10);
          break;
        case 'ArrowRight':
          event.preventDefault();
          video.currentTime = Math.min(video.duration || 0, video.currentTime + 10);
          break;
        case 'ArrowUp':
          event.preventDefault();
          video.volume = Math.min(1, video.volume + 0.1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          video.volume = Math.max(0, video.volume - 0.1);
          break;
        case 'KeyM':
          event.preventDefault();
          video.muted = !video.muted;
          break;
        case 'KeyF':
          event.preventDefault();
          toggleFullscreen();
          break;
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [togglePlay, toggleFullscreen, videoRef]);

  const getSeekPercent = useCallback((clientX: number) => {
    const bar = progressRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const seekTo = useCallback((clientX: number) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    video.currentTime = getSeekPercent(clientX) * video.duration;
  }, [videoRef, getSeekPercent]);

  const updateHover = useCallback((clientX: number) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video?.duration || !bar) return;
    const rect = bar.getBoundingClientRect();
    const pct = getSeekPercent(clientX);
    setHoverTime(pct * video.duration);
    setHoverX(Math.max(0, Math.min(rect.width, clientX - rect.left)));
  }, [videoRef, getSeekPercent]);

  const onProgressPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsSeeking(true);
    seekTo(event.clientX);
    updateHover(event.clientX);
  };

  const onProgressPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    updateHover(event.clientX);
  };

  useEffect(() => {
    if (!isSeeking) return;

    const onMove = (event: PointerEvent) => {
      seekTo(event.clientX);
      updateHover(event.clientX);
    };
    const onUp = () => setIsSeeking(false);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [isSeeking, seekTo, updateHover]);

  const selectQuality = useCallback((idx: number) => {
    if (!hls) return;
    hls.currentLevel = idx;
    setIsAutoMode(idx < 0);
    setShowQuality(false);
  }, [hls]);

  const seekBy = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    const maxTime = video.duration || Number.POSITIVE_INFINITY;
    video.currentTime = Math.max(0, Math.min(maxTime, video.currentTime + seconds));
  }, [videoRef]);

  const getLevelLabel = (idx: number) =>
    idx < 0 || idx >= levels.length ? 'Auto' : `${levels[idx].height}p`;

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const controlsVisible = visible;
  const volumeIcon = isMuted || volume === 0
    ? <VolumeX className="h-5 w-5" />
    : volume < 0.5
      ? <Volume1 className="h-5 w-5" />
      : <Volume2 className="h-5 w-5" />;
  const neoIconButton = 'flex h-9 w-9 items-center justify-center border-2 border-black bg-white text-black shadow-[2px_2px_0_0_#fdc800] transition hover:bg-black hover:text-white hover:border-white';

  return (
    <div
      className="absolute inset-0 z-10 cursor-pointer"
      onMouseEnter={() => {
        isHoveringRef.current = true;
        showControls();
      }}
      onMouseMove={showControls}
      onMouseLeave={() => {
        isHoveringRef.current = false;
        hideControls();
      }}
      onClick={togglePlay}
    >
      {feedback && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center border-4 border-white bg-accent text-black shadow-[6px_6px_0_0_#000] animate-ping-once">
            {feedback === 'play' ? <Play className="h-8 w-8 fill-current" /> : <Pause className="h-8 w-8 fill-current" />}
          </div>
        </div>
      )}

      <div
        className={`pointer-events-none absolute inset-0 flex flex-col justify-end transition-opacity duration-150 ${
          controlsVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div
          className={`${controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'} m-2 px-1 py-2 sm:m-3 sm:px-3 sm:py-3`}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            ref={progressRef}
            className="relative mb-2 h-7 cursor-pointer touch-none sm:mb-3 sm:h-5"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.floor(duration || 0)}
            aria-valuenow={Math.floor(currentTime || 0)}
            tabIndex={0}
            onPointerDown={onProgressPointerDown}
            onPointerMove={onProgressPointerMove}
            onPointerLeave={() => setHoverTime(null)}
          >
            <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 border-2 border-white bg-black">
              <div
                className="absolute inset-y-0 left-0 bg-white/35"
                style={{ width: `${buffered}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 bg-accent"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div
              className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 border-2 border-black bg-accent shadow-[2px_2px_0_0_#fff] transition-transform duration-150"
              style={{ left: `${progressPct}%`, transform: `translate(-50%, -50%) scale(${isSeeking ? 1.15 : 1})` }}
            />
            {hoverTime !== null && (
              <div
                className="pointer-events-none absolute -top-8 -translate-x-1/2 border-2 border-white bg-black px-2 py-1 text-[11px] font-mono text-white shadow-[2px_2px_0_0_#fdc800]"
                style={{ left: hoverX }}
              >
                {formatTime(hoverTime)}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <button
                type="button"
                className={`${neoIconButton} hidden sm:flex`}
                onClick={(event) => {
                  event.stopPropagation();
                  seekBy(-10);
                }}
                title="Back 10 seconds"
                aria-label="Back 10 seconds"
              >
                <SkipBack className="h-5 w-5 fill-current" />
              </button>

              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center border-2 border-black bg-accent text-black shadow-[3px_3px_0_0_#fff] transition hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#fff]"
                onClick={(event) => {
                  event.stopPropagation();
                  togglePlay();
                }}
                title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause className="h-7 w-7 fill-current" /> : <Play className="h-7 w-7 fill-current" />}
              </button>

              <button
                type="button"
                className={`${neoIconButton} hidden sm:flex`}
                onClick={(event) => {
                  event.stopPropagation();
                  seekBy(10);
                }}
                title="Forward 10 seconds"
                aria-label="Forward 10 seconds"
              >
                <SkipForward className="h-5 w-5 fill-current" />
              </button>

              <div className="group/volume ml-0 flex items-center gap-1 sm:ml-1">
                <button
                  type="button"
                  className={neoIconButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleMute();
                  }}
                  title="Mute (M)"
                  aria-label={isMuted || volume === 0 ? 'Unmute' : 'Mute'}
                >
                  {volumeIcon}
                </button>
                <div className="hidden w-0 overflow-hidden transition-all duration-200 group-hover/volume:w-20 sm:block">
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.02}
                    value={isMuted ? 0 : volume}
                    onChange={handleVolume}
                    onClick={(event) => event.stopPropagation()}
                    className="player-volume-slider w-20"
                    aria-label="Volume"
                  />
                </div>
              </div>

              <span className="ml-0 shrink-0 whitespace-nowrap text-[10px] font-mono font-bold text-white/85 min-[380px]:ml-1 sm:text-xs">
                {formatTime(currentTime)}
                <span className="hidden text-white/45 sm:inline"> / {formatTime(duration)}</span>
              </span>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <div className="relative">
                <button
                  type="button"
                  className="flex h-9 items-center gap-1 border-2 border-black bg-white px-2 text-xs font-black text-black shadow-[2px_2px_0_0_#fdc800] transition hover:border-white hover:bg-black hover:text-white"
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowQuality((value) => !value);
                  }}
                  title="Quality settings"
                  aria-label="Quality settings"
                  aria-expanded={showQuality}
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">{isAutoMode ? 'Auto' : getLevelLabel(currentLevel)}</span>
                </button>

                {showQuality && (
                  <div className="absolute bottom-full right-0 mb-3 max-h-64 min-w-36 overflow-y-auto border-2 border-black bg-white py-1 text-black shadow-[4px_4px_0_0_#fdc800]">
                    <div className="border-b-2 border-black px-3 py-2 text-[10px] font-black uppercase text-black/70">
                      Quality
                    </div>
                    <button
                      type="button"
                      className={`flex w-full items-center justify-between gap-4 px-3 py-2 text-left text-xs font-bold transition ${
                        isAutoMode ? 'bg-accent text-black' : 'text-black/70 hover:bg-black hover:text-white'
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        selectQuality(-1);
                      }}
                    >
                      <span>Auto (ABR)</span>
                      {isAutoMode && <span className="text-black">ON</span>}
                    </button>
                    {[...levels].reverse().map((level, reverseIndex) => {
                      const index = levels.length - 1 - reverseIndex;
                      const active = !isAutoMode && currentLevel === index;
                      return (
                        <button
                          key={index}
                          type="button"
                          className={`flex w-full items-center justify-between gap-4 px-3 py-2 text-left text-xs font-bold transition ${
                            active ? 'bg-accent text-black' : 'text-black/70 hover:bg-black hover:text-white'
                          }`}
                          onClick={(event) => {
                            event.stopPropagation();
                            selectQuality(index);
                          }}
                        >
                          <span>{level.height}p</span>
                          {active && <span className="text-black">ON</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                type="button"
                className={neoIconButton}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleFullscreen();
                }}
                title="Fullscreen (F)"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
